import { apiErrorSchema } from '@hugo/core'
import type { z } from 'zod'
import { apiUrl, isNative } from './platform'
import { tokenStore } from './token-store'

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    /** Extra facts the server sent with the error, e.g. `needsLink` and `scopes` on connect. */
    readonly details: Record<string, unknown> = {},
  ) {
    super(message)
    this.name = 'ApiError'
  }
}

/** The ApiError for a failed response body (the API's JSON error shape, or anything else). */
export function apiErrorFrom(status: number, body: unknown): ApiError {
  const parsed = apiErrorSchema.safeParse(body)
  if (!parsed.success) return new ApiError(status, 'http_error', `Request failed (${status})`)
  const { code, message, details } = parsed.data.error
  return new ApiError(status, code, message, details ?? {})
}

/** Headers that authenticate a request: cookie on web (implicit), Bearer token on native. */
export async function authHeaders(): Promise<Record<string, string>> {
  if (!isNative) return {}
  const token = await tokenStore.get()
  return token ? { authorization: `Bearer ${token}` } : {}
}

/** Fetch JSON from the API and validate it with a zod schema. */
export async function apiFetch<S extends z.ZodType>(
  path: string,
  schema: S,
  init: RequestInit & { json?: unknown } = {},
): Promise<z.output<S>> {
  const { json, headers, ...rest } = init
  const response = await fetch(apiUrl(path), {
    ...rest,
    credentials: isNative ? 'omit' : 'same-origin',
    headers: {
      accept: 'application/json',
      ...(json === undefined ? {} : { 'content-type': 'application/json' }),
      ...(await authHeaders()),
      ...headers,
    },
    ...(json === undefined ? {} : { body: JSON.stringify(json) }),
  })
  const body: unknown = await response.json().catch(() => null)
  if (!response.ok) throw apiErrorFrom(response.status, body)
  return schema.parse(body)
}
